import { useState } from "react";
import AgreementsHub from "@/pages/admin/AgreementsHub";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { LogOut, Sparkles, FileText, MessageSquareQuote, CalendarDays, Compass, Mail, Sparkle, ScrollText, BarChart3, DoorOpen, ShoppingBag, Ticket, PlugZap, Users, TrendingUp, Upload, Mic, Layout, MapPin, HandHeart, Shield, Activity, LifeBuoy, Star, Menu, Percent, Send, Banknote, Building2, ListChecks, Store, Trash2, Receipt } from "lucide-react";
import CenterRosterPanel from "@/pages/admin/CenterRosterPanel";
import VendorApplicationsPanel from "@/pages/admin/VendorApplicationsPanel";
import CleanupPanel from "@/pages/admin/CleanupPanel";
import DuplicatesPanel from "@/pages/admin/DuplicatesPanel";
import { BroadcastingHub } from "@/components/media/BroadcastingHub";
import GiftCardsPanel from "@/pages/admin/GiftCardsPanel";
import { Copy as CopyIcon, Gift as GiftIcon, Home as HomeIcon } from "lucide-react";
import RoleHome from "@/components/dashboard/RoleHome";
import { ADMIN_FEATURES } from "@/lib/featureDescriptions";
import { useAuth } from "@/context/AuthContext";
import ContextSwitcher from "@/components/ContextSwitcher";
import { RESOURCES } from "@/pages/admin/fields";
import ResourceManager from "@/pages/admin/ResourceManager";
import SignaturesAudit from "@/pages/admin/SignaturesAudit";
import DigestPanel from "@/pages/admin/DigestPanel";
import FeedbackResponsesPanel from "@/pages/admin/FeedbackResponsesPanel";
import FormsPanel from "@/pages/admin/FormsPanel";
import IntegrationsPanel from "@/pages/admin/IntegrationsPanel";
import AnalyticsPanel from "@/pages/admin/AnalyticsPanel";
import ImportPanel from "@/pages/admin/ImportPanel";
import GaugeContentPanel from "@/pages/admin/GaugeContentPanel";
import VenueApprovalPanel from "@/pages/admin/VenueApprovalPanel";
import GaugeMatchesPanel from "@/pages/admin/GaugeMatchesPanel";
import PageBuilder from "@/pages/admin/PageBuilder";
import UsersPanel from "@/pages/admin/UsersPanel";
import ProfileFieldsPanel from "@/pages/admin/ProfileFieldsPanel";
import LocationsPanel from "@/pages/admin/LocationsPanel";
import StewardshipPanel from "@/pages/admin/StewardshipPanel";
import TestimonialsModerationPanel from "@/pages/admin/TestimonialsModerationPanel";
import DataRequestsPanel from "@/pages/admin/DataRequestsPanel";
import SeoPanel from "@/pages/admin/SeoPanel";
import SystemHealthPanel from "@/pages/admin/SystemHealthPanel";
import HomepageContentPanel from "@/pages/admin/HomepageContentPanel";
import HelpPanel from "@/pages/admin/HelpPanel";
import GaugeSuggestionsPanel from "@/pages/admin/GaugeSuggestionsPanel";
import GoogleReviewsPanel from "@/pages/admin/GoogleReviewsPanel";
import NavPanel from "@/pages/admin/NavPanel";
import MembershipsPanel from "@/pages/admin/MembershipsPanel";
import AdminClientsPanel from "@/pages/admin/AdminClientsPanel";
import SuggestionsPanel from "@/pages/admin/SuggestionsPanel";
import CommissionPanel from "@/pages/admin/CommissionPanel";
import CrmPanel from "@/pages/admin/CrmPanel";
import EmailTemplatesPanel from "@/pages/admin/EmailTemplatesPanel";
import FeedModerationPanel from "@/pages/admin/FeedModerationPanel";
import AdminPayoutsPanel from "@/pages/admin/AdminPayoutsPanel";
import ReconciliationPanel from "@/pages/admin/ReconciliationPanel";
import RevenueSplitPanel from "@/pages/admin/RevenueSplitPanel";
import AppointmentsPanel from "@/pages/admin/AppointmentsPanel";
import OperationsPanel from "@/pages/admin/OperationsPanel";
import SellerOrders from "@/components/SellerOrders";
import SchedulingPanel from "@/pages/admin/SchedulingPanel";
import PayrollPanel from "@/pages/admin/PayrollPanel";
import LocationHubPanel from "@/pages/admin/LocationHubPanel";
import DashboardShell from "@/components/dashboard/DashboardShell";
import ShowcaseModerationPanel from "@/pages/admin/ShowcaseModerationPanel";
import CorporateInquiriesPanel from "@/pages/admin/CorporateInquiriesPanel";
import CouponsOverviewPanel from "@/pages/admin/CouponsOverviewPanel";
import { ProgramHealth, HostedPrograms } from "@/components/programs/ProgramAdmin";
import { AdminReferralAgreements } from "@/components/ReferralAgreements";

const TABS = [
  { key: "home", label: "Home", icon: HomeIcon },
  { key: "commissions", label: "Commissions", icon: Percent },
  { key: "analytics", label: "Analytics", icon: TrendingUp },
  { key: "users", label: "Members", icon: Users },
  { key: "clients", label: "Clients & Purchases", icon: Receipt },
  { key: "profile_fields", label: "Profile Fields", icon: ListChecks },
  { key: "articles", label: "Articles", icon: FileText },
  { key: "broadcasting", label: "Media & Broadcasting", icon: Mic },
  { key: "pages", label: "Page Builder", icon: Layout },
  { key: "homepage", label: "Homepage", icon: Layout },
  { key: "menu", label: "Menu Editor", icon: Menu },
  { key: "memberships", label: "Memberships", icon: Ticket },
  { key: "suggestions", label: "Suggestions", icon: MessageSquareQuote },
  { key: "revenue_split", label: "Revenue Split", icon: Percent },
  { key: "events", label: "Events", icon: CalendarDays },
  { key: "services", label: "Services", icon: Sparkle },
  { key: "coupons", label: "Coupons", icon: Percent },
  { key: "products", label: "Products", icon: ShoppingBag },
  { key: "packages", label: "Packages", icon: Ticket },
  { key: "rooms", label: "Rooms", icon: DoorOpen },
  { key: "locations", label: "Locations", icon: MapPin },
  { key: "venue", label: "Venue Approvals", icon: DoorOpen },
  { key: "center_roster", label: "Center Roster", icon: Building2 },
  { key: "agreements", label: "Agreements", icon: ScrollText },
  { key: "stewardship_ops", label: "Stewardship & Impact Goals", icon: HandHeart },
  { key: "testimonials_mod", label: "Reviews & Testimonials", icon: MessageSquareQuote },
  { key: "data_requests", label: "Privacy", icon: Shield },
  { key: "seo", label: "SEO", icon: TrendingUp },
  { key: "system_health", label: "System Health", icon: Activity },
  { key: "help", label: "Help Center", icon: LifeBuoy },
  { key: "google-reviews", label: "Google Reviews", icon: Star },
  { key: "groups", label: "Groups", icon: Users },
  { key: "programs", label: "Program health", icon: Activity },
  { key: "moderation", label: "Moderation", icon: Shield },
  { key: "payouts", label: "Payouts", icon: Banknote },
  { key: "reconciliation", label: "Reconciliation", icon: Banknote },
  { key: "orders", label: "Orders & Shipping", icon: ShoppingBag },
  { key: "scheduling", label: "Scheduling", icon: CalendarDays },
  { key: "appointments", label: "Appointments", icon: CalendarDays },
  { key: "operations_view", label: "Operations Reports", icon: CalendarDays },
  { key: "payroll", label: "Payroll", icon: Banknote },
  { key: "location_hub", label: "Location Hub", icon: Building2 },
  { key: "gauge", label: "GAUGE Pillars", icon: Compass },
  { key: "gauge-copy", label: "GAUGE Copy", icon: Compass },
  { key: "gauge-suggestions", label: "GAUGE Suggestions", icon: Compass },
  { key: "gauge-matches", label: "GAUGE Matches", icon: TrendingUp },
  { key: "digest", label: "Summary Emails", icon: Mail },
  { key: "crm", label: "Email Campaigns", icon: Send },
  { key: "email_templates", label: "Message Templates", icon: Mail },
  { key: "import", label: "Import", icon: Upload },
  { key: "integrations", label: "Integrations", icon: PlugZap },
  { key: "vendors", label: "Vendor & Enhancer Applications", icon: Store },
  { key: "cleanup", label: "Cleanup", icon: Trash2 },
  { key: "duplicates", label: "Duplicate Review", icon: CopyIcon },
  { key: "gift_cards", label: "Gift Cards & Payments", icon: GiftIcon },
  { key: "showcase", label: "Community Showcase", icon: Users },
  { key: "corporate", label: "Corporate Inquiries", icon: Building2 },
  { key: "feedback", label: "Feedback Surveys", icon: MessageSquareQuote },
  { key: "forms_all", label: "Forms", icon: MessageSquareQuote },
];

// Unified shell navigation: blueprint groups first, then a fold-in "Platform Admin"
// group holding the remaining admin-only tabs so nothing is lost.
const GROUPS = [
  { key: "overview", label: "Overview", icon: Activity, tabs: ["home", "analytics", "system_health"] },
  { key: "schedule", label: "Schedule & Front Desk", icon: CalendarDays, tabs: ["operations_view", "appointments", "scheduling"] },
  { key: "offerings", label: "Offerings & Facilities", icon: Sparkle, tabs: ["services", "products", "packages", "events", "rooms", "locations", "venue", "center_roster"] },
  { key: "people", label: "People & Network", icon: Users, tabs: ["users", "clients", "vendors", "profile_fields", "memberships", "groups", "programs", "moderation", "data_requests"] },
  { key: "finance", label: "Finance & Sales", icon: Banknote, tabs: ["revenue_split", "coupons", "reconciliation", "gift_cards", "commissions", "payouts", "orders", "payroll", "agreements"] },
  { key: "communication", label: "Communication & Feedback", icon: MessageSquareQuote, tabs: ["corporate", "feedback", "forms_all", "suggestions", "testimonials_mod", "google-reviews", "crm", "email_templates", "digest", "showcase"] },
  { key: "platform_admin", label: "Platform Admin", icon: Shield, tabs: ["broadcasting", "articles", "pages", "homepage", "menu", "help", "seo", "gauge", "gauge-copy", "gauge-suggestions", "gauge-matches", "stewardship_ops", "import", "integrations", "location_hub", "cleanup", "duplicates"] },
];
const TAB_BY_KEY = Object.fromEntries(TABS.map((t) => [t.key, t]));

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "home");
  const [apptPreset, setApptPreset] = useState("");
  const partnerAppsQ = useQuery({
    queryKey: ["admin-partner-apps-count"],
    queryFn: () => api.get("/admin/partner-applications?status=pending").then((r) => r.data.length),
    refetchInterval: 60000,
  });
  const pendingPartners = partnerAppsQ.data || 0;
  const vendorAppsQ = useQuery({
    queryKey: ["admin-vendor-apps-count"],
    queryFn: () => api.get("/admin/vendor-applications?status=pending").then((r) => r.data.length),
    refetchInterval: 60000,
  });
  const pendingVendors = vendorAppsQ.data || 0;
  const goToAppointments = (preset = "") => { setApptPreset(preset); setTab("appointments"); };

  const badgeFor = (k) => (k === "locations" ? pendingPartners : k === "vendors" ? pendingVendors : 0);
  const navGroups = GROUPS.map((g) => ({
    key: g.key,
    label: g.label,
    icon: g.icon,
    items: g.tabs.map((k) => TAB_BY_KEY[k]).filter(Boolean).map((t) => ({ key: t.key, label: t.label, icon: t.icon, badge: badgeFor(t.key), desc: ADMIN_FEATURES[t.key] })),
  }));
  const quickActions = [
    { label: "My Enhancer Profile", icon: Sparkle, onClick: () => nav("/enhancer"), testid: "admin-studio-btn" },
    { label: "Council & Revenue", icon: BarChart3, onClick: () => nav("/council"), testid: "admin-council-btn" },
    { label: "Front Desk / POS", icon: Sparkles, onClick: () => nav("/pos"), testid: "admin-pos-btn", primary: true },
  ];

  return (
    <DashboardShell
      role="admin"
      brandTitle="Ecosystem Control Room"
      scopeName="Platform-wide · All Centers"
      nav={navGroups}
      activeKey={tab}
      onSelect={setTab}
      contextSwitcher={<ContextSwitcher />}
      quickActions={quickActions}
      user={user}
      onLogout={() => { logout(); nav("/"); }}
    >
      <div data-testid="admin-dashboard-content">
        {tab === "home" ? (
          <RoleHome endpoint="/dashboard/home" queryKey={["admin-home"]} title={`Welcome back${user?.name ? `, ${user.name.split(" ")[0]}` : ""}`}
            go={(k) => (k === "unread" ? nav("/messages") : k === "approvals" ? setTab(pendingVendors ? "vendors" : pendingPartners ? "locations" : "appointments") : goToAppointments())}
            quickLinks={[
              { label: "Front desk / POS", onClick: () => nav("/pos") },
              { label: "Appointments", onClick: () => setTab("appointments") },
              { label: "Events", onClick: () => setTab("events") },
              { label: "Members", onClick: () => setTab("users") },
              { label: "Media & Broadcasting", onClick: () => setTab("broadcasting") },
              { label: "Payouts", onClick: () => setTab("payouts") },
              { label: "Messages", onClick: () => nav("/messages") },
            ]} />
        ) : tab === "digest" ? (
          <DigestPanel />
        ) : tab === "feedback" ? (
          <FeedbackResponsesPanel />
        ) : tab === "forms_all" ? (
          <FormsPanel />
        ) : tab === "users" ? (
          <UsersPanel />
        ) : tab === "profile_fields" ? (
          <ProfileFieldsPanel />
        ) : tab === "locations" ? (
          <LocationsPanel />
        ) : tab === "stewardship_ops" ? (
          <div className="space-y-12"><StewardshipPanel /><ResourceManager resourceKey="stewardship" config={RESOURCES.stewardship} /></div>
        ) : tab === "testimonials_mod" ? (
          <div className="space-y-12"><TestimonialsModerationPanel /><ResourceManager resourceKey="testimonials" config={RESOURCES.testimonials} /></div>
        ) : tab === "data_requests" ? (
          <DataRequestsPanel />
        ) : tab === "seo" ? (
          <SeoPanel />
        ) : tab === "system_health" ? (
          <SystemHealthPanel />
        ) : tab === "integrations" ? (
          <IntegrationsPanel />
        ) : tab === "analytics" ? (
          <AnalyticsPanel onDrillAppointments={goToAppointments} />
        ) : tab === "appointments" ? (
          <AppointmentsPanel initialPreset={apptPreset} />
        ) : tab === "operations_view" ? (
          <OperationsPanel />
        ) : tab === "import" ? (
          <ImportPanel />
        ) : tab === "gauge-copy" ? (
          <GaugeContentPanel />
        ) : tab === "venue" ? (
          <VenueApprovalPanel />
        ) : tab === "center_roster" ? (
          <CenterRosterPanel />
        ) : tab === "gauge-matches" ? (
          <GaugeMatchesPanel />
        ) : tab === "pages" ? (
          <PageBuilder />
        ) : tab === "homepage" ? (
          <HomepageContentPanel />
        ) : tab === "help" ? (
          <HelpPanel />
        ) : tab === "gauge-suggestions" ? (
          <GaugeSuggestionsPanel />
        ) : tab === "google-reviews" ? (
          <GoogleReviewsPanel />
        ) : tab === "menu" ? (
          <NavPanel />
        ) : tab === "memberships" ? (
          <MembershipsPanel />
        ) : tab === "clients" ? (
          <AdminClientsPanel />
        ) : tab === "suggestions" ? (
          <SuggestionsPanel />
        ) : tab === "revenue_split" ? (
          <RevenueSplitPanel />
        ) : tab === "commissions" ? (
          <><CommissionPanel /><AdminReferralAgreements /></>
        ) : tab === "crm" ? (
          <CrmPanel />
        ) : tab === "email_templates" ? (
          <EmailTemplatesPanel />
        ) : tab === "moderation" ? (
          <FeedModerationPanel />
        ) : tab === "programs" ? (
          <><ProgramHealth /><HostedPrograms /></>
        ) : tab === "payouts" ? (
          <AdminPayoutsPanel />
        ) : tab === "reconciliation" ? (
          <ReconciliationPanel />
        ) : tab === "orders" ? (
          <SellerOrders />
        ) : tab === "scheduling" ? (
          <SchedulingPanel />
        ) : tab === "payroll" ? (
          <PayrollPanel />
        ) : tab === "location_hub" ? (
          <LocationHubPanel />
        ) : tab === "vendors" ? (
          <VendorApplicationsPanel />
        ) : tab === "cleanup" ? (
          <CleanupPanel />
        ) : tab === "broadcasting" ? (
          <div className="space-y-10"><BroadcastingHub /><ResourceManager resourceKey="media" config={RESOURCES.media} /></div>
        ) : tab === "duplicates" ? (
          <DuplicatesPanel />
        ) : tab === "gift_cards" ? (
          <GiftCardsPanel />
        ) : tab === "showcase" ? (
          <ShowcaseModerationPanel />
        ) : tab === "corporate" ? (
          <CorporateInquiriesPanel />
        ) : tab === "coupons" ? (
          <CouponsOverviewPanel />
        ) : tab === "agreements" ? (
          <AgreementsHub />
        ) : (
          <ResourceManager resourceKey={tab} config={RESOURCES[tab]} />
        )}
      </div>
    </DashboardShell>
  );
}
